import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import PieChart, { PieChartProps } from "@cloudscape-design/components/pie-chart"
import Popover, { type PopoverProps } from "@cloudscape-design/components/popover"
import ProgressBar from "@cloudscape-design/components/progress-bar"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import { ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import type { DashboardStats, StudyHealthBuckets } from "../../services/daeApi"
import "./DaeDashboard.css"

/**
 * Semantic traffic-light colours for the Study Health donut segments — the clean, vibrant dataviz
 * status palette (red = worst → green = best). These are fixed, mode-invariant hex values validated
 * for both light and dark surfaces; do NOT swap them for Cloudscape's `colorChartsStatus*` tokens,
 * which render muddy (brownish red / olive / dull green).
 */
const STUDY_HEALTH_COLORS = {
  atRisk: "#d03b3b",
  watch: "#fab219",
  healthy: "#0ca30c",
} as const

/**
 * Weighted portfolio health score (0-100): Healthy counts full, Watch half, At Risk zero. Returns
 * null when no studies are graded (nothing to score). Exported for unit testing.
 */
export const studyHealthScore = (health: StudyHealthBuckets): number | null => {
  const total = health.atRisk + health.watch + health.healthy
  if (total === 0) return null
  return Math.round(((health.healthy + health.watch * 0.5) / total) * 100)
}

interface HealthPanelProps {
  stats: DashboardStats
  loading: boolean
  studyMetrics: { totalStudies: string; activeStudies: string; studiesNeedingAttention: string }
  studiesLoading: boolean
  attentionLoading: boolean
}

interface StatMetric {
  value: ReactNode
  label: string
  /**
   * Show a spinner in place of the value while it loads.
   */
  loading?: boolean
  /**
   * Optional deep link rendered beneath this metric (e.g. straight to its filtered list).
   */
  linkText?: string
  onLink?: () => void
}

const StatLink = ({ text, onClick }: { text: string; onClick: () => void }) => (
  <Button variant="inline-link" iconName="arrow-right" iconAlign="right" onClick={onClick}>
    {text}
  </Button>
)

/**
 * Small "info" affordance: an inline info icon that opens a Popover. Used to define the health
 * buckets and explain the weighted score — the beta finding was that both read as unexplained
 * jargon in the UI.
 */
const InfoPopover = ({
  header,
  ariaLabel,
  children,
  position = "top",
  renderWithPortal = false,
}: {
  header: string
  ariaLabel: string
  children: ReactNode
  position?: PopoverProps.Position
  /**
   * Render into a body-level portal. Needed when the trigger sits inside a narrow container (e.g.
   * the ring's centre overlay), so the popover uses its own `size="large"` width instead of
   * collapsing to the container width.
   */
  renderWithPortal?: boolean
}) => (
  <Popover
    triggerType="custom"
    position={position}
    size="large"
    renderWithPortal={renderWithPortal}
    header={header}
    content={children}
  >
    <Button variant="inline-icon" iconName="status-info" ariaLabel={ariaLabel} />
  </Popover>
)

/**
 * Popover body shared by both Study Health info affordances: a plain intro line followed by
 * bold-label rows (`<label>: <value>`). Both the bucket-grading and score-weighting popovers are
 * this same shape, so they share one definition instead of two near-identical blocks.
 */
interface DefinitionRow {
  label: string
  value: string
}

const InfoDefinitionList = ({ intro, rows }: { intro: string; rows: DefinitionRow[] }) => (
  <SpaceBetween size="xs">
    <Box variant="small">{intro}</Box>
    {rows.map((row) => (
      <Box key={row.label} variant="small">
        <b>{row.label}</b>
        {`: ${row.value}`}
      </Box>
    ))}
  </SpaceBetween>
)

// Study Health grading buckets, best to worst, defined once for the header info popover.
const HEALTH_BUCKET_DEFINITIONS: DefinitionRow[] = [
  { label: "Healthy", value: "90% or higher" },
  { label: "Watch", value: "50–90%" },
  { label: "At Risk", value: "under 50%" },
]

// Per-study point weighting behind the 0–100 health score, for the score info popover.
const HEALTH_SCORE_WEIGHTS: DefinitionRow[] = [
  { label: "Healthy study", value: "1 pt" },
  { label: "Studies on watch", value: "1/2 pt" },
  { label: "At-risk studies", value: "0 pt" },
]

/**
 * A stat tile with one metric, or two split by a divider. Each metric can carry its own deep link
 * (rendered beneath it); a tile may instead use a single centred footer link. The Studies + Vendors
 * pair drives the health row's height; the donut card stretches to match them.
 */
const StatTilePair = ({
  title,
  left,
  right,
  footerLinkText,
  onFooterLink,
}: {
  title: string
  left: StatMetric
  right?: StatMetric
  footerLinkText?: string
  onFooterLink?: () => void
}) => (
  <Container header={<Header variant="h3">{title}</Header>}>
    <SpaceBetween size="l">
      <div className="health-stat-row">
        {(right ? [left, right] : [left]).map((m) => (
          <div key={m.label}>
            {/* The spinner sits outside the large-value text: inside it, the spinner's arcs
                inherit the large font size and orbit off-centre. */}
            {m.loading ? (
              <div className="health-stat-loading">
                <Spinner size="large" />
              </div>
            ) : (
              <Box variant="awsui-value-large">{m.value}</Box>
            )}
            <Box color="text-body-secondary">{m.label}</Box>
            {m.linkText && m.onLink && (
              <Box padding={{ top: "l" }}>
                <StatLink text={m.linkText} onClick={m.onLink} />
              </Box>
            )}
          </div>
        ))}
      </div>
      {footerLinkText && onFooterLink && (
        <Box textAlign="center">
          <StatLink text={footerLinkText} onClick={onFooterLink} />
        </Box>
      )}
    </SpaceBetween>
  </Container>
)

const HealthPanel = ({
  stats,
  loading,
  studyMetrics,
  studiesLoading,
  attentionLoading,
}: HealthPanelProps) => {
  const navigate = useNavigate()

  // Studies bucketed by recent ingestion-run pass rate (server-side aggregation). Only studies with
  // graded run activity land in a bucket, so the ring sums to "studies actively receiving data" —
  // studies with no runs yet carry no health signal and are left out.
  //
  // Segment colours are the semantic traffic-light palette (red = worst → green = best), set as the
  // per-segment `color` on the PieChart datum. See STUDY_HEALTH_COLORS above for why these are
  // explicit hex rather than Cloudscape status tokens.
  const healthBuckets = [
    { title: "At Risk", value: stats.studies.health.atRisk, color: STUDY_HEALTH_COLORS.atRisk },
    { title: "Watch", value: stats.studies.health.watch, color: STUDY_HEALTH_COLORS.watch },
    { title: "Healthy", value: stats.studies.health.healthy, color: STUDY_HEALTH_COLORS.healthy },
  ]

  const gradedStudyTotal = healthBuckets.reduce((sum, b) => sum + b.value, 0)

  // All three buckets stay in the legend (colour is never the only cue) even when a bucket is zero;
  // a zero bucket renders no arc but keeps its label. When nothing is graded we pass no data so the
  // donut falls back to its "No data yet" empty state.
  const studyHealthData: PieChartProps.Datum[] =
    gradedStudyTotal === 0
      ? []
      : healthBuckets.map((b) => ({ title: b.title, value: b.value, color: b.color }))

  const score = studyHealthScore(stats.studies.health)
  const scoreDisplay = score === null ? "—" : String(score)

  // DTA progress: share of DTAs that are active. Every DTA that isn't active is still pending.
  const pendingDtas = stats.dtas.total - stats.dtas.active
  const dtaActivePercent =
    stats.dtas.total === 0 ? 0 : Math.round((stats.dtas.active / stats.dtas.total) * 100)

  return (
    <Container
      header={
        <Header variant="h2" description="Topline metrics for your study portfolio">
          Health
        </Header>
      }
    >
      <div className="health-tiles">
        <div className="health-left-col">
          <StatTilePair
            title="Studies"
            left={{
              value: studyMetrics.totalStudies,
              loading: studiesLoading,
              label: "Total",
              linkText: "View all studies",
              onLink: () => navigate("/studies"),
            }}
            right={{
              value: studyMetrics.studiesNeedingAttention,
              loading: attentionLoading,
              label: "Need attention",
              linkText: "View studies",
              onLink: () => navigate("/studies?status=NEEDS_ATTENTION"),
            }}
          />

          <StatTilePair
            title="Vendors"
            left={{ value: stats.vendors.active, label: "Active", loading }}
            footerLinkText="Manage vendors"
            onFooterLink={() => navigate("/vendors")}
          />
        </div>

        <Container
          fitHeight
          header={
            <Header
              variant="h3"
              info={
                <InfoPopover
                  header="How Study Health is graded"
                  ariaLabel="How Study Health is graded"
                >
                  <InfoDefinitionList
                    intro="Studies are rated by run pass rate (past 14 days)"
                    rows={HEALTH_BUCKET_DEFINITIONS}
                  />
                </InfoPopover>
              }
            >
              Study Health
            </Header>
          }
        >
          <div className="health-donut-body">
            {/* One PieChart for both states: when nothing is graded `studyHealthData` is [] and the
                chart shows its own `empty` slot ("No data yet"); otherwise it draws the ring. The
                score overlay is gated on graded studies. The overlay is centred on the ring
                container (top/left 50% + translate), which lines up with the ring centre because a
                legend-less PieChart centres its pie in the whole plot box. */}
            <div className="health-donut-chart">
              <div className="health-donut-ring">
                <PieChart
                  variant="donut"
                  fitHeight
                  hideFilter
                  hideTitles
                  hideDescriptions
                  hideLegend
                  data={studyHealthData}
                  statusType={loading ? "loading" : "finished"}
                  ariaLabel="Study health by ingestion run pass rate"
                  empty={
                    <Box textAlign="center" color="text-body-secondary">
                      No data yet
                    </Box>
                  }
                />
                {gradedStudyTotal > 0 && (
                  <div className="health-donut-center">
                    <div className="health-score-number">{scoreDisplay}</div>
                    <div className="health-score-label">
                      <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
                        <Box color="text-body-secondary" fontSize="body-s">
                          Health score
                        </Box>
                        <InfoPopover
                          header="Health score"
                          ariaLabel="What is the health score?"
                          position="bottom"
                          renderWithPortal
                        >
                          <InfoDefinitionList
                            intro="Overall health of your studies."
                            rows={HEALTH_SCORE_WEIGHTS}
                          />
                        </InfoPopover>
                      </SpaceBetween>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {gradedStudyTotal > 0 && (
              <div className="health-donut-legend">
                {healthBuckets.map((b) => (
                  <span key={b.title} className="health-legend-item">
                    <span
                      className="health-legend-swatch"
                      style={{ background: b.color }}
                      aria-hidden="true"
                    />
                    {b.title}
                  </span>
                ))}
              </div>
            )}
            <Box padding={{ top: "s" }}>
              <ProgressBar
                value={dtaActivePercent}
                label={<Box fontWeight="bold">DTAs</Box>}
                additionalInfo={`${stats.dtas.active} active · ${pendingDtas} pending`}
              />
            </Box>
            <Box textAlign="center" padding={{ top: "s" }}>
              <Button
                variant="inline-link"
                iconName="arrow-right"
                iconAlign="right"
                onClick={() => navigate("/studies?status=NEEDS_ATTENTION")}
              >
                View studies needing attention
              </Button>
            </Box>
          </div>
        </Container>
      </div>
    </Container>
  )
}

export default HealthPanel
